import { FieldOption } from '../FieldBank/_types';

export interface FieldOptionValidationResult {
  isValid: boolean;
  errors: {
    minOptions?: string;
    duplicateLabels?: string;
    emptyLabels?: string;
  };
}

/**
 * Validates field options for SingleSelect fields
 * Checks for minimum options, duplicate labels, and empty labels
 */
export const validateFieldOptions = (options: FieldOption[]): FieldOptionValidationResult => {
  const errors: FieldOptionValidationResult['errors'] = {};

  // Filter to only active options with labels for validation
  const validOptions =
    options?.filter((opt: FieldOption) => opt.status === 'Active' && opt.label && opt.label.trim() !== '') || [];

  // Check for minimum 2 options
  if (validOptions.length < 2) {
    errors.minOptions = 'At least 2 options with labels are required';
  }

  // Check for duplicate labels (case-insensitive)
  if (validOptions.length > 0) {
    const labels = validOptions.map((opt: FieldOption) => opt.label.trim().toLowerCase());
    const uniqueLabels = new Set(labels);
    if (labels.length !== uniqueLabels.size) {
      errors.duplicateLabels = 'Please remove duplicate options';
    }
  }

  // Check for empty labels in active options
  const hasEmptyLabels = options?.some(
    (opt: FieldOption) => opt.status === 'Active' && (!opt.label || opt.label.trim() === '')
  );
  if (hasEmptyLabels) {
    errors.emptyLabels = 'Field option labels cannot be empty';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Gets a user-friendly error message from validation result
 */
export const getValidationErrorMessage = (validationResult: FieldOptionValidationResult): string => {
  if (validationResult.isValid) return '';

  const { errors } = validationResult;

  // Return the first error found (prioritize in order of importance)
  if (errors.emptyLabels) return errors.emptyLabels;
  if (errors.duplicateLabels) return errors.duplicateLabels;
  if (errors.minOptions) return errors.minOptions;

  return 'Invalid field options';
};
